import React from "react";
import { TanstackSortingFn } from "../../../utils/TableSorting";
import CustomBadge from "../../../components/ui/CustomBadge/CustomBadge";
import BaseTable from "../../../components/ui/CustomTanStackTable/CustomTanStackTable";
import ColumnVisibilitySelector from "../../../components/ui/ColumnVisibilitySelector/ColumnVisibilitySelector";
import useColumnVisibilityStore, {
  selectVisibleColumns,
} from "../../../stores/columnVisibilityStore";
import { TRANSACTION_TABLE_STATUS_MAP } from "../../Transactions/constants/transactionConstants";
import { getPaymentMethod } from "../../Transactions/utils/transactionFormatters";
import {
  formatDateTimeToLocale,
  formatKES,
  getDisplayValue,
} from "../../../utils/transactionutils";

const DEFAULT_COLUMN_KEYS = ["gatewayReference", "description", "status"];

const OPTIONAL_COLUMN_KEYS = [
  "amount",
  "transactionCharges",
  "transactionDate",
  "accountNumber",
  "paymentMethod",
];

const COLUMN_META = [
  { key: "gatewayReference", label: "Gateway Ref" },
  { key: "description", label: "Description" },
  { key: "status", label: "Status" },
  { key: "amount", label: "Amount" },
  { key: "transactionCharges", label: "Charges" },
  { key: "transactionDate", label: "Date" },
  { key: "accountNumber", label: "Account Number" },
  { key: "paymentMethod", label: "Payment Type" },
];

function DashboardTableTanStack({
  data,
  pagination: parentPagination,
  onPaginationChange,
  isLoading = false,
  isFetching = false,
}) {
  const [pagination, setPagination] = React.useState(
    parentPagination || { pageIndex: 0, pageSize: 10 },
  );

  const visibleOptionalKeys = useColumnVisibilityStore(
    selectVisibleColumns("transactions"),
  );
  const setVisibleOptionalKeys = useColumnVisibilityStore(
    (s) => s.setVisibleColumns,
  );

  React.useEffect(() => {
    if (parentPagination) {
      setPagination(parentPagination);
    }
  }, [parentPagination]);

  const columnVisibility = React.useMemo(() => {
    const visibility = {};
    OPTIONAL_COLUMN_KEYS.forEach((key) => {
      visibility[key] = visibleOptionalKeys.includes(key);
    });
    return visibility;
  }, [visibleOptionalKeys]);

  const columns = React.useMemo(
    () => [
      // Always-visible core columns
      {
        accessorKey: "gatewayReference",
        header: "Gateway Ref",
        cell: (info) => (
          <div
            className="max-w-[140px] truncate"
            title={getDisplayValue(info.getValue())}
          >
            {getDisplayValue(info.getValue())}
          </div>
        ),
        sortingFn: TanstackSortingFn,
      },
      {
        accessorKey: "description",
        header: "Description",
        cell: (info) => (
          <div className="max-w-[240px] whitespace-normal break-words leading-snug py-1">
            {getDisplayValue(info.getValue())}
          </div>
        ),
        sortingFn: TanstackSortingFn,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (info) => {
          const backendStatus = info.getValue();
          const mappedStatus = TRANSACTION_TABLE_STATUS_MAP[backendStatus];
          return (
            <CustomBadge variant="light" color={mappedStatus} size="sm">
              {backendStatus}
            </CustomBadge>
          );
        },
        size: 120,
        minSize: 100,
      },
      // Optional columns
      {
        accessorKey: "amount",
        header: "Amount",
        cell: (info) => (
          <div className="truncate" title={formatKES(info.getValue())}>
            {formatKES(info.getValue())}
          </div>
        ),
        sortingFn: TanstackSortingFn,
      },
      {
        accessorKey: "transactionCharges",
        header: "Charges",
        cell: (info) => (
          <div className="truncate" title={formatKES(info.getValue())}>
            {formatKES(info.getValue())}
          </div>
        ),
        sortingFn: TanstackSortingFn,
      },
      {
        accessorKey: "transactionDate",
        header: "Date",
        cell: (info) => (
          <div
            className="max-w-[140px] truncate"
            title={formatDateTimeToLocale(info.getValue())}
          >
            {formatDateTimeToLocale(info.getValue())}
          </div>
        ),
        sortingFn: TanstackSortingFn,
      },
      {
        accessorKey: "accountNumber",
        header: "Account Number",
        cell: (info) => (
          <div
            className="max-w-[120px] truncate"
            title={getDisplayValue(info.getValue())}
          >
            {getDisplayValue(info.getValue())}
          </div>
        ),
        sortingFn: TanstackSortingFn,
      },
      {
        id: "paymentMethod",
        header: "Payment Type",
        cell: (info) => {
          const { serviceCode, payoutAccountType, gatewayReference } =
            info.row.original;
          const value = getPaymentMethod(
            serviceCode,
            payoutAccountType,
            gatewayReference,
          );
          return (
            <div className="max-w-[150px] truncate" title={value}>
              {value}
            </div>
          );
        },
        enableSorting: false,
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end">
        <ColumnVisibilitySelector
          columns={COLUMN_META}
          defaultColumnKeys={DEFAULT_COLUMN_KEYS}
          visibleColumnKeys={visibleOptionalKeys}
          onChange={(keys) => setVisibleOptionalKeys("transactions", keys)}
        />
      </div>
      <BaseTable
        data={data}
        isLoading={isLoading}
        isFetching={isFetching}
        columns={columns}
        columnVisibility={columnVisibility}
        pageSize={pagination.pageSize}
        pagination={pagination}
        onPaginationChange={(newPagination) => {
          setPagination(newPagination);
          if (typeof onPaginationChange === "function")
            onPaginationChange(newPagination);
        }}
        paginationMode="client"
        showPaginationNavigation={false}
        emptyMessage="No Recent Transactions Found"
        rowClassName={() =>
          "hover:bg-blue-50 cursor-pointer bg-white-50 min-h-[50px] "
        }
      />
    </div>
  );
}

export default DashboardTableTanStack;
